import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import { apiRequest } from '@/lib/api';
import type { AdminUser, AdminUserList, UserStatusFilter, UserWithAccessSync } from '@/types/users';

export const ADMIN_USERS_KEY = ['admin', 'users'] as const;

/** Sprint 17: `enabled` lets the Team report skip it for Supervisors (Admin-only endpoint). */
export function useAdminUsers(status: UserStatusFilter, enabled = true): UseQueryResult<AdminUserList> {
  return useQuery({
    queryKey: [...ADMIN_USERS_KEY, status],
    queryFn: () => apiRequest<AdminUserList>(`/api/admin/users?status=${status}`),
    enabled,
  });
}

function useInvalidateUsers(): () => Promise<void> {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ADMIN_USERS_KEY });
}

export function useChangeRole(): UseMutationResult<{ user: AdminUser }, Error, { id: string; role: 'admin' | 'user' }> {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: ({ id, role }) =>
      apiRequest<{ user: AdminUser }>(`/api/admin/users/${id}/role`, { method: 'PATCH', body: JSON.stringify({ role }) }),
    onSuccess: invalidate,
  });
}

export function useChangeStartDate(): UseMutationResult<{ user: AdminUser }, Error, { id: string; startDate: string }> {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: ({ id, startDate }) =>
      apiRequest<{ user: AdminUser }>(`/api/admin/users/${id}/start-date`, { method: 'PATCH', body: JSON.stringify({ startDate }) }),
    onSuccess: invalidate,
  });
}

export type StatusAction = 'deactivate' | 'reactivate' | 'access-sync';

/** Deactivate / reactivate / retry access sync: all return { user, accessSync }. */
export function useUserStatusAction(): UseMutationResult<UserWithAccessSync, Error, { id: string; action: StatusAction }> {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: ({ id, action }) => apiRequest<UserWithAccessSync>(`/api/admin/users/${id}/${action}`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}
